/**
 * The display list: what a laid-out page IS, independent of the output
 * format. Both backends (SVG preview, PDF) draw exactly this, so the preview
 * is the print.
 *
 * Units: page millimeters, origin top-left, y down. Group transforms must be
 * rigid (rotation + translation): stroke widths and font sizes are already in
 * paper millimeters and must not be scaled.
 */

import type { LegendRow, PlanLayerCategory, PrintColor, StrokeStyle } from 'shared-types';
import type { Matrix } from './geometry';

export type PathCommand = { op: 'M'; x: number; y: number } | { op: 'L'; x: number; y: number } | { op: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number } | { op: 'Z' };

export interface PathItem {
  type: 'path';
  commands: PathCommand[];
  stroke: StrokeStyle | null;
  fill: PrintColor | null;
  fillRule: 'nonzero' | 'evenodd';
  /** The plan layer it was drawn on (plan content only): legends read the drawing's colors by layer. */
  layer?: PlanLayerCategory;
}

/** One line of text, already laid out (no wrapping left to do). */
export interface TextItem {
  type: 'text';
  /** Baseline start. */
  x: number;
  y: number;
  text: string;
  /** FontRegistry key of the exact face (family + weight + style). */
  font: string;
  /** Em size, millimeters. */
  sizeMm: number;
  color: PrintColor;
  letterSpacingMm: number;
  /** The plan layer it was drawn on (plan content only). */
  layer?: PlanLayerCategory;
  /** Clockwise around (x, y). */
  rotationDeg: number;
}

export interface ImageItem {
  type: 'image';
  /** Key into the image resources handed to the backend. */
  image: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ClipRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GroupItem {
  type: 'group';
  /** Rigid; applied to the group's items (and its clip). */
  transform: Matrix | null;
  clip: ClipRect | null;
  items: DisplayItem[];
}

export type DisplayItem = PathItem | TextItem | ImageItem | GroupItem;

export interface DisplayPage {
  widthMm: number;
  heightMm: number;
  items: DisplayItem[];
  /** The rows each legend on the page shows, by legend element id (the sheet editor offers them for naming). */
  legends?: Record<string, LegendRow[]>;
}

// ─── Builders ───────────────────────────────────────────────────────────

export function group(items: DisplayItem[], transform: Matrix | null = null, clip: ClipRect | null = null): GroupItem {
  return { type: 'group', transform, clip, items };
}

export function rectPath(x: number, y: number, w: number, h: number): PathCommand[] {
  return [{ op: 'M', x, y }, { op: 'L', x: x + w, y }, { op: 'L', x: x + w, y: y + h }, { op: 'L', x, y: y + h }, { op: 'Z' }];
}

/** Bézier circle constant: control distance / radius for a quarter arc. */
const KAPPA = 0.5522847498307936;

export function ellipsePath(cx: number, cy: number, rx: number, ry: number): PathCommand[] {
  const kx = rx * KAPPA;
  const ky = ry * KAPPA;
  return [
    { op: 'M', x: cx + rx, y: cy },
    { op: 'C', x1: cx + rx, y1: cy + ky, x2: cx + kx, y2: cy + ry, x: cx, y: cy + ry },
    { op: 'C', x1: cx - kx, y1: cy + ry, x2: cx - rx, y2: cy + ky, x: cx - rx, y: cy },
    { op: 'C', x1: cx - rx, y1: cy - ky, x2: cx - kx, y2: cy - ry, x: cx, y: cy - ry },
    { op: 'C', x1: cx + kx, y1: cy - ry, x2: cx + rx, y2: cy - ky, x: cx + rx, y: cy },
    { op: 'Z' },
  ];
}

export function roundedRectPath(x: number, y: number, w: number, h: number, radius: number): PathCommand[] {
  const r = Math.min(radius, w / 2, h / 2);
  if (r <= 0) return rectPath(x, y, w, h);
  const k = r * (1 - KAPPA);
  return [
    { op: 'M', x: x + r, y },
    { op: 'L', x: x + w - r, y },
    { op: 'C', x1: x + w - k, y1: y, x2: x + w, y2: y + k, x: x + w, y: y + r },
    { op: 'L', x: x + w, y: y + h - r },
    { op: 'C', x1: x + w, y1: y + h - k, x2: x + w - k, y2: y + h, x: x + w - r, y: y + h },
    { op: 'L', x: x + r, y: y + h },
    { op: 'C', x1: x + k, y1: y + h, x2: x, y2: y + h - k, x, y: y + h - r },
    { op: 'L', x, y: y + r },
    { op: 'C', x1: x, y1: y + k, x2: x + k, y2: y, x: x + r, y },
    { op: 'Z' },
  ];
}

/** Font keys and image keys a page uses (what the backend must load). */
export function collectResources(items: readonly DisplayItem[], out: { fonts: Set<string>; images: Set<string> } = { fonts: new Set(), images: new Set() }): { fonts: Set<string>; images: Set<string> } {
  for (const item of items) {
    if (item.type === 'text') out.fonts.add(item.font);
    else if (item.type === 'image') out.images.add(item.image);
    else if (item.type === 'group') collectResources(item.items, out);
  }
  return out;
}
